import { useOrgModules } from "@/modules/useOrgModules";
import { CheckCircle2, GitMerge, Receipt, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useDelete, useNotify, useRefresh } from "ra-core";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { useMemberCapability } from "@/components/atomic-crm/providers/commons/useMemberCapability";
import type { Ticket } from "@/modules/types";
import { TicketBulkCreateInvoiceDialog } from "@/modules/tickets/TicketBulkCreateInvoiceDialog";
import { TicketBulkMergeDialog } from "@/modules/tickets/TicketBulkMergeDialog";
import { TicketBulkResolveDialog } from "@/modules/tickets/TicketBulkResolveDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type TicketInboxBulkBarProps = {
  selectedTickets: Ticket[];
  companyById: Map<string, Company>;
  contactById: Map<string, Contact>;
  onClear: () => void;
  onMerged: (primaryTicketId: string | number) => void;
};

export const TicketInboxBulkBar = ({
  selectedTickets,
  companyById,
  contactById,
  onClear,
  onMerged,
}: TicketInboxBulkBarProps) => {
  const [mergeOpen, setMergeOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne] = useDelete();
  const canManageTickets = useMemberCapability("support.tickets.manage");
  // Member may send invoices AND the company has Invoices on (Apps).
  const { isEnabled: isOrgModuleEnabled } = useOrgModules();
  const canSendInvoices =
    useMemberCapability("proposals.send") && isOrgModuleEnabled("invoices");
  const canBulkInvoice = canManageTickets && canSendInvoices;

  if (!selectedTickets.length) return null;

  const count = selectedTickets.length;

  const runDelete = async () => {
    setPending(true);
    try {
      for (const ticket of selectedTickets) {
        await deleteOne("tickets", {
          id: ticket.id,
          previousData: ticket,
        });
      }
      notify(
        count === 1
          ? "Ticket moved to Deleted"
          : `Moved ${count} tickets to Deleted`,
        { type: "success" },
      );
      onClear();
      refresh();
    } catch {
      notify("Could not delete selected tickets", { type: "error" });
    } finally {
      setPending(false);
      setDeleteOpen(false);
    }
  };

  const runResolve = () => setResolveOpen(true);

  return (
    <>
      <Card className="fixed bottom-2 left-0 right-0 z-50 mx-auto flex w-[90%] flex-col items-stretch gap-2 bg-muted p-2 px-4 sm:w-fit md:flex-row md:items-center md:gap-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            className="has-[>svg]:px-0"
            aria-label="Clear selection"
            onClick={onClear}
          >
            <X className="size-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {count} ticket{count === 1 ? "" : "s"} selected
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canBulkInvoice ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8"
              disabled={count < 2 || pending}
              onClick={() => setInvoiceOpen(true)}
            >
              <Receipt className="size-3.5" />
              Create invoice
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8"
            disabled={count < 2 || pending}
            onClick={() => setMergeOpen(true)}
          >
            <GitMerge className="size-3.5" />
            Merge
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8"
            disabled={pending}
            onClick={() => void runResolve()}
          >
            <CheckCircle2 className="size-3.5" />
            Mark resolved
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 text-destructive hover:text-destructive"
            disabled={pending}
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="size-3.5" />
            Delete
          </Button>
        </div>
      </Card>

      <TicketBulkCreateInvoiceDialog
        open={invoiceOpen}
        onOpenChange={setInvoiceOpen}
        tickets={selectedTickets}
        companyById={companyById}
        contactById={contactById}
        onComplete={onClear}
      />

      <TicketBulkMergeDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        tickets={selectedTickets}
        onMerged={(primaryTicketId) => {
          onClear();
          onMerged(primaryTicketId);
        }}
      />

      <TicketBulkResolveDialog
        open={resolveOpen}
        tickets={selectedTickets}
        onOpenChange={setResolveOpen}
        onResolved={onClear}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete {count} ticket{count === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription>
              These tickets move to Deleted. You can restore them from the
              Deleted list. Messages stay with each ticket.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={pending}
              onClick={() => void runDelete()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
